import React, { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FleetCourt, FleetResponse, getSponsors, getVideosToday, postCommand,
  waitCommand, addSponsor, removeSponsor, patchCourtConfig,
} from '../../api/gestor';
import { Icon, Modal, useToast, fmtAgo, serviceIcon, serviceLabel } from './ui';

function statusText(state?: string): { cls: string; label: string } {
  if (state === 'active') return { cls: 'up', label: 'up' };
  if (state === 'activating' || state === 'reloading') return { cls: 'degraded', label: state };
  return { cls: 'down', label: state || 'down' };
}

/* ============== card de uma quadra (câmera/subcourt) ============== */
function QuadraCard({
  court, cam, onOpen,
}: { court: FleetCourt; cam: { cam: any; subcourt?: string }; onOpen: () => void }) {
  const n = Number(cam.cam);
  const services = court.services || {};
  const mine = Object.keys(services).filter((s) => s.includes(`-cam-${n}`) || s.includes(`-cam-${n}-`));
  const downCount = mine.filter((s) => services[s] !== 'active').length;
  const offline = court.status === 'offline';
  const dot = offline || downCount > 1 ? 'bad' : downCount === 1 ? 'warn' : 'ok';
  return (
    <button className="qcard" onClick={onOpen}>
      <div className="qh">
        <div>
          <div className="qn">{cam.subcourt || `Câmera ${n}`}</div>
          <div className="qe">câmera {n} · {court.identity?.court}</div>
        </div>
        <span className={`dot ${dot}`} />
      </div>
      <div className="qstats">
        <div className="qstat"><b>{offline ? '—' : `${Math.round(court.cpu_pct || 0)}%`}</b>cpu</div>
        <div className="qstat"><b>{offline ? '—' : `${Math.round(court.temp_c || 0)}°`}</b>temp</div>
        <div className="qstat"><b>{mine.length - downCount}/{mine.length}</b>services</div>
      </div>
      <div className="qserv">
        {mine.map((s) => (
          <i key={s} className={offline ? 'bad' : services[s] === 'active' ? '' : 'bad'} />
        ))}
      </div>
    </button>
  );
}

/* ============== drawer de detalhe da arena/quadra ============== */
export function CourtDrawer({
  court, onClose, refetchFleet,
}: { court: FleetCourt; onClose: () => void; refetchFleet: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busyService, setBusyService] = useState<string | null>(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagText, setDiagText] = useState('');
  const [diagBusy, setDiagBusy] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [newName, setNewName] = useState(court.identity?.court || '');
  const [renameBusy, setRenameBusy] = useState(false);
  const [addCamOpen, setAddCamOpen] = useState(false);
  const [rtsp, setRtsp] = useState('');
  const [subcourt, setSubcourt] = useState('');
  const [gpio, setGpio] = useState('');
  const [addCamBusy, setAddCamBusy] = useState(false);
  const [confirmReboot, setConfirmReboot] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [sponsorBusy, setSponsorBusy] = useState(false);
  const [removeCam, setRemoveCam] = useState<number | null>(null);
  const buttonSystem = court.config?.button_system || 'gpio';

  const setButtonSystem = async (system: 'gpio' | 'arduino') => {
    if (system === buttonSystem) return;
    try {
      await patchCourtConfig(court.slug, { button_system: system });
      toast(system === 'arduino'
        ? 'Marcado como Arduino — button-cam parado deixa de contar como erro'
        : 'Marcado como GPIO');
      refetchFleet();
    } catch (e: any) {
      toast(`Erro: ${e.message}`, 'warn');
    }
  };

  const offline = court.status === 'offline';
  const services = court.services || {};

  const { data: videos } = useQuery({
    queryKey: ['videos-today', court.slug],
    queryFn: () => getVideosToday(court.slug),
    enabled: !offline,
    staleTime: 5 * 60 * 1000,
  });

  const { data: sponsorData } = useQuery({
    queryKey: ['sponsors', court.slug],
    queryFn: () => getSponsors(court.slug),
    staleTime: 60 * 1000,
  });

  async function runCommand(type: string, params: any, label: string): Promise<boolean> {
    try {
      const { commandId } = await postCommand(court.slug, type, params);
      toast(`${label} enviado…`, 'info');
      const result = await waitCommand(court.slug, commandId);
      if (result.status === 'done') {
        toast(`${label}: concluído`);
        refetchFleet();
        return true;
      }
      toast(`${label}: ${result.status === 'error' ? 'erro' : 'sem resposta (quadra offline?)'}`, 'warn');
      if (result.result) { setDiagText(result.result); setDiagOpen(true); }
      return false;
    } catch (e: any) {
      toast(`${label}: ${e.message}`, 'warn');
      return false;
    }
  }

  const resetService = async (svc: string) => {
    setBusyService(svc);
    await runCommand('restart_service', { service: svc }, `Reset ${serviceLabel(svc)}`);
    setBusyService(null);
  };

  const runDiagnostics = async () => {
    setDiagBusy(true);
    setDiagOpen(true);
    setDiagText('Executando diagnóstico na quadra…');
    try {
      const { commandId } = await postCommand(court.slug, 'diagnostics', {});
      const result = await waitCommand(court.slug, commandId);
      setDiagText(result.result || `status: ${result.status}`);
    } catch (e: any) {
      setDiagText(`erro: ${e.message}`);
    }
    setDiagBusy(false);
  };

  const doRename = async () => {
    const name = newName.trim();
    if (!name) return;
    setRenameBusy(true);
    const ok = await runCommand('set_court_name', { court: name }, 'Renomear quadra');
    setRenameBusy(false);
    if (ok) {
      setRenameOpen(false);
      toast('Nome atualizado nos scripts das câmeras. Atenção: os próximos vídeos vão para a nova pasta no bucket.', 'info');
    }
  };

  const doAddCamera = async () => {
    if (!rtsp.trim() || !subcourt.trim() || !gpio.trim()) {
      toast('Preencha nome, RTSP e GPIO do botão', 'warn');
      return;
    }
    setAddCamBusy(true);
    const ok = await runCommand('add_camera',
      { rtsp_url: rtsp.trim(), subcourt: subcourt.trim(), gpio: gpio.trim() },
      'Adicionar quadra');
    setAddCamBusy(false);
    if (ok) { setAddCamOpen(false); setRtsp(''); setSubcourt(''); setGpio(''); }
  };

  const onSponsorFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSponsorBusy(true);
    try {
      await addSponsor(court.slug, file);
      toast('Patrocínio enviado — atualizando a quadra…');
      queryClient.invalidateQueries({ queryKey: ['sponsors', court.slug] });
    } catch (err: any) {
      toast(`Erro: ${err.message}`, 'warn');
    }
    setSponsorBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onRemoveSponsor = async (file: string) => {
    setSponsorBusy(true);
    try {
      await removeSponsor(court.slug, file);
      toast('Patrocínio removido — quadra sendo atualizada');
      queryClient.invalidateQueries({ queryKey: ['sponsors', court.slug] });
    } catch (err: any) {
      toast(`Erro: ${err.message}`, 'warn');
    }
    setSponsorBusy(false);
  };

  return (
    <>
      <div className="drawer-back open" onClick={onClose} />
      <aside className="drawer open">
        <div className="drawer-head">
          <div className="dtop">
            <span className={`dot ${offline ? 'bad' : court.status === 'degraded' ? 'warn' : 'ok'}`} />
            <div className="dname">{court.identity?.court || court.slug}</div>
            <button className="drawer-close" onClick={onClose}><Icon name="x" /></button>
          </div>
          <div className="dsub">
            <span>{court.identity?.city} / {court.identity?.state}</span>
            <span className="pill"><span className="k">host</span> {court.hostname || court.slug}</span>
            <span className="pill"><span className="k">visto</span> {fmtAgo(court.last_seen)}</span>
            <span className="pill"><span className="k">agente</span> v{court.agent_version || '—'}</span>
          </div>
        </div>

        <div className="drawer-body">
          <h4>Métricas agora</h4>
          <div className="metric-row">
            <div className="metric"><div className="ml">CPU</div>
              <div className="mv">{offline ? '—' : `${Math.round(court.cpu_pct || 0)}%`}</div></div>
            <div className="metric"><div className="ml">Temperatura</div>
              <div className="mv">{offline ? '—' : `${Math.round(court.temp_c || 0)}°C`}</div></div>
            <div className="metric"><div className="ml">Vídeos hoje</div>
              <div className="mv green">{videos ? videos.total : '—'}</div></div>
            <div className="metric"><div className="ml">Memória</div>
              <div className="mv">{offline ? '—' : `${Math.round(court.mem_pct || 0)}%`}</div></div>
            <div className="metric"><div className="ml">Disco</div>
              <div className="mv">{offline ? '—' : `${Math.round(court.disk_pct || 0)}%`}</div></div>
            <div className="metric"><div className="ml">Fila</div>
              <div className="mv">{Number(court.queue_depth || 0)}</div></div>
          </div>

          {videos && Object.keys(videos.by_subcourt || {}).length > 0 && (
            <>
              <h4>Vídeos por quadra hoje</h4>
              {Object.entries(videos.by_subcourt).map(([name, n]) => (
                <div key={name} className="srow">
                  <span className="sicon"><Icon name="film" /></span>
                  <div><div className="sname">{name}</div></div>
                  <div className="sright"><span className="sstatus up mono">{n} vídeos</span></div>
                </div>
              ))}
            </>
          )}

          <h4>Services</h4>
          {Object.keys(services).length === 0 && (
            <div className="report-empty">Sem informação de services (quadra ainda não reportou).</div>
          )}
          {Object.entries(services).sort(([a], [b]) => a.localeCompare(b)).map(([svc, state]) => {
            const st = statusText(offline ? 'down' : state);
            return (
              <div key={svc} className="srow">
                <span className="sicon"><Icon name={serviceIcon(svc)} /></span>
                <div>
                  <div className="sname">{serviceLabel(svc)}</div>
                  <div className="sdet">{svc}</div>
                </div>
                <div className="sright">
                  <span className={`sstatus ${st.cls}`}>{st.label}</span>
                  <button
                    className="btn-reset"
                    title="Resetar service"
                    disabled={busyService === svc || offline}
                    onClick={() => resetService(svc)}>
                    {busyService === svc ? <span className="spinner" /> : <Icon name="reset" size={15} />}
                  </button>
                </div>
              </div>
            );
          })}

          <h4>Câmeras / quadras deste rasp</h4>
          {(court.cams || []).map((cam: any) => {
            const n = Number(cam.cam);
            const mine = Object.keys(services).filter((s) => s.match(new RegExp(`-cam-${n}(-|$)`)));
            const removed = mine.length === 0;
            return (
              <div key={n} className="cam-row">
                <span className="sicon"><Icon name="cam" /></span>
                <div>
                  <div className="sname">{cam.subcourt || `Câmera ${n}`}</div>
                  <div className="sdet">câmera {n} · {removed ? 'services removidos' : `${mine.length} services`}</div>
                </div>
                <div className="sright" style={{ marginLeft: 'auto' }}>
                  {removed ? (
                    <button className="btn btn-ghost btn-sm" disabled={offline}
                      onClick={() => runCommand('restore_camera', { cam: n }, `Restaurar câmera ${n}`)}>
                      Restaurar
                    </button>
                  ) : (
                    <button className="iconbtn" title="Remover câmera (desativa os services dela — use quando a quadra não existe física)"
                      disabled={offline}
                      onClick={() => setRemoveCam(n)}>
                      <Icon name="trash" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <h4>Sistema de botão</h4>
          <div className="toggle-row">
            <span className="sicon"><Icon name="spark" /></span>
            <div>
              <div className="sname">{buttonSystem === 'arduino' ? 'Arduino nano (listenerbutton)' : 'GPIO (button-cam)'}</div>
              <div className="sdet">
                {buttonSystem === 'arduino'
                  ? 'button-cam parado NÃO conta como erro nesta quadra'
                  : 'button-cam parado conta como erro'}
              </div>
            </div>
            <div className="seg">
              <button className={buttonSystem !== 'arduino' ? 'on' : ''}
                onClick={() => setButtonSystem('gpio')}>GPIO</button>
              <button className={buttonSystem === 'arduino' ? 'on' : ''}
                onClick={() => setButtonSystem('arduino')}>Arduino</button>
            </div>
          </div>

          <h4>Patrocínios (parte inferior do vídeo)</h4>
          <div className="sp-preview">
            <span className="sp-label">prévia · posição no vídeo final</span>
            {(sponsorData?.sponsors || []).filter((s) => s.previewUrl).map((s) => (
              <img key={s.file} src={s.previewUrl!} alt={s.file}
                className={`sp-logo ${s.slot || 'bottom_center'}`} />
            ))}
            {!(sponsorData?.sponsors || []).length && (
              <span className="sp-empty">sem patrocínios — os logos aparecem aqui embaixo</span>
            )}
          </div>
          {(sponsorData?.sponsors || []).map((s) => (
            <div key={s.file} className="sponsor-row">
              {s.previewUrl
                ? <img src={s.previewUrl} alt={s.file} />
                : <span className="sicon"><Icon name="img" /></span>}
              <div>
                <div className="sname">{s.file}</div>
                <div className="sp-slot">{(s.slot || '').replace(/_/g, ' ')}</div>
              </div>
              <div className="sright" style={{ marginLeft: 'auto' }}>
                <button className="iconbtn" title="Remover patrocínio"
                  disabled={sponsorBusy} onClick={() => onRemoveSponsor(s.file)}>
                  <Icon name="trash" />
                </button>
              </div>
            </div>
          ))}
          {!(sponsorData?.sponsors || []).length && (
            <div className="report-empty">
              Nenhum patrocínio gerenciado ainda{court.process_queue_version === 'legacy'
                ? ' (esta quadra usa o overlay legado — o primeiro patrocínio adicionado migra automaticamente)' : ''}.
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }} onChange={onSponsorFile} />
          <button className="btn btn-ghost btn-sm" disabled={sponsorBusy}
            onClick={() => fileRef.current?.click()}>
            {sponsorBusy ? <span className="spinner" /> : <Icon name="plus" size={14} />}
            Adicionar patrocínio
          </button>

          <h4>Diagnóstico</h4>
          <button className="btn btn-ghost btn-sm" onClick={runDiagnostics} disabled={diagBusy || offline}>
            {diagBusy ? <span className="spinner" /> : <Icon name="search" size={14} />}
            Executar diagnóstico de erros
          </button>
          {diagOpen && <pre className="diag-pre">{diagText}</pre>}
        </div>

        <div className="drawer-foot">
          <button className="btn btn-ghost" onClick={() => setRenameOpen(true)} disabled={offline}>
            <Icon name="edit" size={14} /> Alterar nome
          </button>
          <button className="btn btn-ghost" onClick={() => setAddCamOpen(true)} disabled={offline}>
            <Icon name="plus" size={14} /> Adicionar quadra
          </button>
          <button className="btn btn-orange full" onClick={() => setConfirmReboot(true)} disabled={offline}>
            <Icon name="power" size={14} /> Reiniciar quadra (reboot)
          </button>
        </div>
      </aside>

      <Modal
        title="Alterar nome da quadra"
        desc="Edita o COURT nos scripts das câmeras deste rasp e reinicia os services de botão. O nome define a pasta no bucket — os vídeos novos vão para a pasta nova."
        open={renameOpen} onClose={() => setRenameOpen(false)}
        onConfirm={doRename} confirmText="Salvar" busy={renameBusy}>
        <label>Novo nome</label>
        <input value={newName} onChange={(e) => setNewName(e.target.value)}
          placeholder="Ex: Arena MV" />
      </Modal>

      <Modal
        title="Adicionar quadra (nova câmera)"
        desc="Cria a câmera no rasp replicando os padrões existentes: script de botão + buffers de vídeo + services."
        open={addCamOpen} onClose={() => setAddCamOpen(false)}
        onConfirm={doAddCamera} confirmText="Criar" busy={addCamBusy}>
        <label>Nome da quadra (sem espaços, ex: Gol3)</label>
        <input value={subcourt} onChange={(e) => setSubcourt(e.target.value)} placeholder="Gol3" />
        <label style={{ marginTop: 14 }}>Host/URL RTSP da câmera (usado nos 2 buffers)</label>
        <input value={rtsp} onChange={(e) => setRtsp(e.target.value)}
          placeholder="rtsp://usuario:senha@192.168.x.x:554/..." />
        <label style={{ marginTop: 14 }}>GPIO do botão da quadra</label>
        <input type="number" value={gpio} onChange={(e) => setGpio(e.target.value)}
          placeholder="ex: 17" />
      </Modal>

      <Modal
        title={`Remover câmera ${removeCam}?`}
        desc="Para quadras que não existem fisicamente (ex: serviço dando erro de uma câmera que não está instalada). Desativa button-cam e buffers dessa câmera no rasp, com backup antes. Reversível pelo botão Restaurar."
        open={removeCam !== null}
        onClose={() => setRemoveCam(null)}
        confirmText="Remover services" confirmClass="btn-danger"
        onConfirm={async () => {
          const n = removeCam;
          setRemoveCam(null);
          if (n !== null) await runCommand('remove_camera', { cam: n }, `Remover câmera ${n}`);
        }} />

      <Modal
        title="Reiniciar a quadra?"
        desc="O rasp vai reiniciar e ficar ~1 min fora. Gravações em andamento são perdidas."
        open={confirmReboot} onClose={() => setConfirmReboot(false)}
        confirmText="Reiniciar" confirmClass="btn-danger"
        onConfirm={async () => {
          setConfirmReboot(false);
          await runCommand('reboot', {}, 'Reboot');
        }} />
    </>
  );
}

/* ============== aba Quadras ============== */
export function QuadrasTab({
  fleet, openSlug, setOpenSlug, refetchFleet,
}: {
  fleet?: FleetResponse;
  openSlug: string | null;
  setOpenSlug: (s: string | null) => void;
  refetchFleet: () => void;
}) {
  if (!fleet) return <div className="loading-page"><span className="spinner" /></div>;
  const open = fleet.courts.find((c) => c.slug === openSlug) || null;
  return (
    <>
      <div className="arena-list">
        {fleet.courts.map((court) => {
          const cams = (court.cams && court.cams.length)
            ? court.cams
            : Array.from({ length: Number(court.cameras || 0) }, (_, i) => ({ cam: i + 1 }));
          const initials = (court.identity?.court || court.slug).slice(0, 2).toUpperCase();
          return (
            <div key={court.slug} className="arena" data-arena-block={court.slug}>
              <div className="arena-top">
                <div className="ai">{initials}</div>
                <div className="ameta">
                  <div className="an">{court.identity?.court || court.slug}</div>
                  <div className="as">
                    {court.identity?.city} / {court.identity?.state} · {cams.length} câmeras ·
                    branch {court.git_branch || '—'} · visto {fmtAgo(court.last_seen)}
                  </div>
                </div>
                <div className="aspace">
                  <span className={`pill ${court.status === 'online' ? 'ok' : court.status === 'degraded' ? 'warn' : 'bad'}`}>
                    {court.status}
                  </span>
                  {(court.services_down || []).length > 0 && (
                    <span className="pill warn">{(court.services_down || []).length} service(s) fora</span>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpenSlug(court.slug)}>
                    Gerenciar
                  </button>
                </div>
              </div>
              <div className="quadra-row">
                {cams.map((cam: any) => (
                  <QuadraCard key={String(cam.cam)} court={court} cam={cam}
                    onOpen={() => setOpenSlug(court.slug)} />
                ))}
              </div>
            </div>
          );
        })}
        {!fleet.courts.length && (
          <div className="report-empty">Nenhuma quadra conectada ainda — instale o agente nos rasps.</div>
        )}
      </div>
      {open && (
        <CourtDrawer court={open} onClose={() => setOpenSlug(null)} refetchFleet={refetchFleet} />
      )}
    </>
  );
}
